import { useCallback, useMemo } from "react";
import type { JobMetadata, Service } from "@/types/api";

interface ServiceUsageEntry {
  name: string;
  running: boolean;
}

function buildJobStatusMap(jobs: JobMetadata[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const job of jobs) {
    map[job.name] = job.state.toLowerCase() === "running";
  }
  return map;
}

function trackStageUsage(
  usage: Record<string, Set<string>>,
  serviceName: string,
  jobName: string,
) {
  if (!usage[serviceName]) usage[serviceName] = new Set();
  usage[serviceName].add(jobName);
}

function buildPipelineUsageMap(
  jobs: JobMetadata[],
): Record<string, Set<string>> {
  const usage: Record<string, Set<string>> = {};

  for (const job of jobs) {
    const pipeline = job.pipeline;
    if (!pipeline) continue;

    trackStageUsage(usage, pipeline.source.service_name, job.name);

    for (const mw of pipeline.middlewares || []) {
      trackStageUsage(usage, mw.service_name, job.name);
    }
    for (const sink of pipeline.sinks) {
      trackStageUsage(usage, sink.service_name, job.name);
    }
  }
  return usage;
}

function buildServiceUsage(
  service: Service,
  pipelineUsage: Record<string, Set<string>>,
  statusMap: Record<string, boolean>,
): ServiceUsageEntry[] {
  const jobNames = new Set<string>([
    ...(service.used_by_jobs || []),
    ...(pipelineUsage[service.name] || []),
  ]);

  return Array.from(jobNames).map((jobName) => ({
    name: jobName,
    running: statusMap[jobName] ?? false,
  }));
}

export function useServiceData(jobs: JobMetadata[] | null) {
  const jobStatusMap = useMemo(
    () => (jobs ? buildJobStatusMap(jobs) : {}),
    [jobs],
  );

  const pipelineUsageMap = useMemo(
    () => (jobs ? buildPipelineUsageMap(jobs) : {}),
    [jobs],
  );

  const getServiceUsage = useCallback(
    (service: Service): ServiceUsageEntry[] =>
      buildServiceUsage(service, pipelineUsageMap, jobStatusMap),
    [jobStatusMap, pipelineUsageMap],
  );

  return { getServiceUsage };
}
